import EditIcon from "@mui/icons-material/Edit";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCityWhitelabelMappings,
  getWhiteLabel,
} from "../../../actions/cities";
import {
  getRewardPrograms,
  getRewardRules,
  updateRewardProgramAsync,
} from "../../../actions/rewards";
import TopUpBonusCampaignModal from "../../../components/modal/TopUpBonusCampaignModal";
import useCustomTable from "../../../components/table/CustomTable";
import { useShowToast } from "../../../hooks";
import useFleetsByPaymentConfig from "../../../hooks/useFleetsByPaymentConfig";
import { isSuperAdmin } from "../../../utils/utils";

const TopUpBonusesContainer = () => {
  const dispatch = useDispatch();
  const showToast = useShowToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const adminDetails = useSelector((state) => state.auth.adminDetails);
  const { getFleetName } = useFleetsByPaymentConfig();
  const cityWhitelabelMapping = useSelector(
    (state) => state.cities.cityWhitelabelMapping
  );
  const {
    rewardPrograms,
    rewardProgramsSuccess,
    rewardRules,
    rewardRulesSuccess,
  } = useSelector((state) => state.rewards);

  useEffect(() => {
    dispatch(getWhiteLabel());
    dispatch(getCityWhitelabelMappings());
    dispatch(getRewardPrograms());
    dispatch(getRewardRules({ trigger_type: "WALLET_TOPPED_UP" }));
  }, [dispatch]);

  const topUpBonusPrograms = useMemo(() => {
    if (!rewardPrograms?.content || !rewardRules?.content) return [];

    const topUpRulesByProgram = rewardRules.content
      .filter(
        (rule) => rule.triggerType === "WALLET_TOPPED_UP" && !rule.isArchived
      )
      .reduce((acc, rule) => {
        if (!acc[rule.rewardProgramId]) {
          acc[rule.rewardProgramId] = [];
        }
        acc[rule.rewardProgramId].push(rule);
        return acc;
      }, {});

    return rewardPrograms.content
      .filter((program) => topUpRulesByProgram[program.id])
      .map((program) => ({
        ...program,
        rules: topUpRulesByProgram[program.id] || [],
        tiersCount: topUpRulesByProgram[program.id]?.length || 0,
      }));
  }, [rewardPrograms, rewardRules]);

  const filteredPrograms = useMemo(() => {
    if (!topUpBonusPrograms || topUpBonusPrograms.length === 0) return [];

    if (isSuperAdmin(adminDetails)) {
      return topUpBonusPrograms;
    }

    const adminWhitelabelId = adminDetails?.whiteLabelId;
    if (!adminWhitelabelId || !cityWhitelabelMapping) {
      return topUpBonusPrograms;
    }

    const whitelabel = cityWhitelabelMapping.find(
      (w) => w.id === adminWhitelabelId
    );
    if (!whitelabel || !whitelabel.paymentConfigurations?.length) {
      return topUpBonusPrograms;
    }

    const paymentConfigIds = whitelabel.paymentConfigurations.map(
      (pc) => pc.id
    );

    return topUpBonusPrograms.filter((program) =>
      paymentConfigIds.includes(program.organizationPaymentConfigurationId)
    );
  }, [topUpBonusPrograms, adminDetails, cityWhitelabelMapping]);

  const handleToggleActive = async (program) => {
    const result = await dispatch(
      updateRewardProgramAsync(program.id, { is_active: !program.isActive })
    );
    if (result.success) {
      showToast(
        `Campaign ${program.isActive ? "deactivated" : "activated"} successfully`,
        "success"
      );
    } else {
      showToast("Failed to update campaign", "error");
    }
    dispatch(getRewardPrograms());
  };

  const handleEditProgram = (program) => {
    setSelectedProgram(program);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProgram(null);
    dispatch(getRewardPrograms());
    dispatch(getRewardRules({ trigger_type: "WALLET_TOPPED_UP" }));
  };

  const columns = [
    {
      name: "Campaign Name",
      selector: "name",
      sortable: true,
      cell: (row) => (
        <Typography variant="body2" fontWeight="medium">
          {row.name || "Unnamed Campaign"}
        </Typography>
      ),
    },
    {
      name: "Fleet",
      selector: "organizationPaymentConfigurationId",
      sortable: true,
      cell: (row) => (
        <Typography variant="body2" color="text.secondary">
          {getFleetName(row.organizationPaymentConfigurationId)}
        </Typography>
      ),
    },
    {
      name: "Tiers",
      selector: "tiersCount",
      sortable: true,
      center: true,
      cell: (row) => (
        <Chip
          label={`${row.tiersCount} tier${row.tiersCount !== 1 ? "s" : ""}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      name: "Status",
      selector: "isActive",
      sortable: true,
      center: true,
      cell: (row) => (
        <Chip
          label={row.isActive ? "Active" : "Inactive"}
          size="small"
          color={row.isActive ? "success" : "default"}
        />
      ),
    },
    {
      name: "Date Range",
      selector: "startDate",
      sortable: true,
      cell: (row) => {
        const start = row.startDate
          ? new Date(row.startDate).toLocaleDateString()
          : "No start";
        const end = row.endDate
          ? new Date(row.endDate).toLocaleDateString()
          : "No end";
        return (
          <Typography variant="body2" color="text.secondary">
            {start} - {end}
          </Typography>
        );
      },
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit Campaign">
            <IconButton size="small" onClick={() => handleEditProgram(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.isActive ? "Deactivate" : "Activate"}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(row)}
              color={row.isActive ? "success" : "default"}
            >
              {row.isActive ? (
                <ToggleOnIcon fontSize="small" />
              ) : (
                <ToggleOffIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleRefresh = () => {
    dispatch(getRewardPrograms());
    dispatch(getRewardRules({ trigger_type: "WALLET_TOPPED_UP" }));
  };

  const { renderCustomTable, renderHeaderBar, setLoading } = useCustomTable(
    columns,
    { content: filteredPrograms, totalElements: filteredPrograms.length },
    handleRefresh,
    () => setIsModalOpen(true),
    [],
    "Top-Up Bonuses",
    false,
    true
  );

  const isLoading = !rewardProgramsSuccess || !rewardRulesSuccess;

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure bonus campaigns that reward users when they top up their
        wallet. Each campaign can have multiple tiers with different minimum
        amounts and bonus rewards.
      </Typography>

      {renderHeaderBar()}
      {renderCustomTable()}

      {isModalOpen && (
        <TopUpBonusCampaignModal
          open={isModalOpen}
          onClose={handleCloseModal}
          program={selectedProgram}
        />
      )}
    </Box>
  );
};

export default TopUpBonusesContainer;
